/* SpawnAnything — block list for figure / building descriptions (shared by the browser and both servers).
   Deliberately small and cheap (user decision 02.10.2026: a block list, no AI moderation). It stops the obvious:
   Nazi / hate symbols and slurs, sexual content, terror and mass-shooting fantasies, child abuse. Real people as friendly
   caricatures stay allowed (the generator prompt asks for that).
   Matching runs on a normalised text: lower case, umlauts and accents folded, leetspeak undone (0->o, 1->i, 3->e, ...).
   - IN_WORD: inside one word ("Nazizombie", "Hitlerjugend"), never across words ("Kanone Gerät" is not "neger")
   - WORD:    only the whole word, for short or ambiguous terms ("sex" in "Sussex", "ss" in "Schloss", "nackt" in "Nacktmull")
   - PHRASE:  long, distinctive phrases matched with spaces and punctuation removed ("school shooter", "H i t l e r") */

const IN_WORD = [
  // national socialism and hate symbols
  'hitler', 'hakenkreuz', 'swastika', 'reichsfuehrer', 'reichsfuhrer', 'gestapo', 'himmler', 'goebbels', 'auschwitz',
  'holocaust', 'neonazi', 'judenvernicht', 'gaskammer', 'konzentrationslager', 'nsdap',
  // slurs
  'nigger', 'nigga', 'neger', 'kanake', 'kanacke', 'zigeuner', 'faggot', 'schwuchtel', 'tranny', 'untermensch',
  // sexual content
  'porno', 'pornhub', 'hentai', 'vagina', 'penis', 'titten', 'boobs', 'orgasm', 'masturb', 'vergewaltig', 'sexy',
  'stripper', 'bordell', 'prostitu', 'dildo', 'onlyfans', 'erotik', 'erotic', 'fetisch', 'fetish', 'bdsm', 'kamasutra',
  // child abuse
  'kinderporn', 'paedophil', 'pedophil', 'kindesmissbrauch',
  // terror and mass shootings
  'amoklauf', 'schulmassaker', 'terroranschlag', 'breivik', 'taliban', 'selbstmordattent',
];
const WORD = ['nazi', 'nazis', 'ss', 'sa', 'kkk', 'sex', 'porn', 'nude', 'nudes', 'naked', 'nackt', 'nackte', 'nackter', 'nacktes',
  'rape', 'rapist', 'tits', 'nsfw', 'xxx', '18+', 'loli', 'kike', 'hure', 'whore', 'slut', 'fick', 'ficken', 'fuck'];
// word beginnings ("Nazizombie", "Nazioffizier"), with harmless exceptions
const PREFIX = ['nazi'], PREFIX_OK = ['nazir', 'nazira'];
const PHRASE = ['hitler', 'kukluxklan', 'siegheil', 'heilhitler', 'whitepower', 'whitesupremac', 'arischerasse', 'gaschamber',
  'schoolshoot', 'schoolmassacre', 'suicidebomb', 'alqaida', 'alqaeda', 'islamicstate', 'islamischerstaat', 'brentontarrant',
  'childporn', 'waffenss'];

const LEET = { 0: 'o', 1: 'i', 3: 'e', 4: 'a', 5: 's', 7: 't', 8: 'b', '@': 'a', $: 's', '!': 'i', '€': 'e' };

/** Lower case, folded umlauts / accents (ä -> ae, é -> e), leetspeak undone. */
export function normalizeText(s) {
  return String(s || '').toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[013457@$!€8]/g, c => LEET[c] || c);
}
const words = s => s.split(/[^a-z0-9+]+/).filter(Boolean);

/** null when the text is fine, otherwise the matched term (for logs and tests, never shown to the player). */
export function blockedTerm(text) {
  const raw = String(text || '');
  const plain = words(raw.toLowerCase()); // "18+" keeps its digits
  for (const w of WORD) if (plain.includes(w)) return w;
  const n = normalizeText(raw), ws = words(n);
  for (const w of WORD) if (ws.includes(w)) return w;
  for (const t of IN_WORD) if (ws.some(w => w.includes(t))) return t;
  for (const t of PREFIX) if (ws.some(w => w.startsWith(t) && !PREFIX_OK.includes(w))) return t;
  const compact = n.replace(/[^a-z]/g, '');
  for (const t of PHRASE) if (compact.includes(t)) return t;
  return null;
}
export const isBlocked = text => blockedTerm(text) !== null;

/** German message for the player. */
export const BLOCKED_MESSAGE = 'Diese Beschreibung ist nicht erlaubt. Probier etwas anderes.';
